import { type Destination, routes } from '../routes';
import { type TranslationKey } from '../i18n';

/**
 * The platform's information architecture, declared once.
 *
 * The mega menu and the footer are two renderings of the same site map. Held
 * separately they diverge within a release — a destination is added to one,
 * quietly missing from the other, and nobody notices because both look
 * plausible. Declared here, adding a page means adding it in one place.
 *
 * ## Group titles are translation keys, link labels are not
 *
 * A group heading is shell chrome and belongs in the dictionary. A link label
 * is the destination's own name, which already lives on the route and is what
 * `DestinationLink` announces as "coming soon". Duplicating those names into
 * the dictionary would fork every page title into two systems; when the
 * destination pages become translatable content they will carry their own
 * translated names with them.
 */

export interface NavigationGroup {
  readonly key: string;
  readonly titleKey: TranslationKey;
  /**
   * The content block that can rename this group.
   *
   * Separate from `titleKey`: the translation key is the shipped name in each
   * language, and this is an editor's override of it. An edited name wins,
   * which is what "the menu is editable from the dashboard" has to mean.
   */
  readonly contentKey: string;
  readonly links: readonly Destination[];
}

/** Services a person on the consumer side can use. */
const services: NavigationGroup = {
  key: 'services',
  titleKey: 'menu.services',
  contentKey: 'menu.group-services',
  links: [
    routes.medicinesPharmacy,
    routes.prescriptionMedicines,
    routes.pharmacyMedicines,
    routes.everydayHealth,
    routes.consultations,
    routes.appointments,
    routes.labTests,
    routes.healthChecks,
    routes.pharmacyServices,
    routes.careServices,
    routes.beautyWellness,
    routes.petHealth,
    routes.delivery,
    routes.collection,
    routes.send,
    routes.penny,
  ],
};

/** Ways to bring a business onto the platform. */
const business: NavigationGroup = {
  key: 'business',
  titleKey: 'menu.business',
  contentKey: 'menu.group-business',
  links: [
    routes.listPharmacy,
    routes.listHealthcareBusiness,
    routes.joinClinician,
    routes.careWellnessProviders,
    routes.deliveryPartners,
    routes.suppliers,
    routes.enterprise,
    routes.partnerPortal,
  ],
};

/** Pharmacy OS — the operating system a dispensing business runs on. */
const pharmacy: NavigationGroup = {
  key: 'pharmacy',
  titleKey: 'menu.pharmacy',
  contentKey: 'menu.group-pharmacy',
  links: [
    routes.pharmacyOs,
    routes.osPrescriptions,
    routes.osDispensing,
    routes.osInventory,
    routes.osPickPack,
    routes.osNms,
    routes.osPatient360,
    routes.osPmr,
    routes.osCompliance,
    routes.osProcurement,
    routes.osReports,
    routes.apiIntegrations,
  ],
};

const company: NavigationGroup = {
  key: 'company',
  titleKey: 'menu.company',
  contentKey: 'menu.group-company',
  links: [routes.about, routes.careers, routes.partnersWithUs, routes.press, routes.news, routes.contact],
};

const help: NavigationGroup = {
  key: 'help',
  titleKey: 'menu.help',
  contentKey: 'menu.group-help',
  links: [
    routes.helpCentre,
    routes.deliveryInformation,
    routes.returns,
    routes.privacy,
    routes.terms,
    routes.accessibility,
    routes.cookies,
  ],
};

/** The shop, which the footer lists separately from services. */
const shop: NavigationGroup = {
  key: 'shop',
  titleKey: 'menu.shop',
  contentKey: 'menu.group-shop',
  links: [
    routes.prescriptionMedicines,
    routes.overTheCounter,
    routes.vitamins,
    routes.healthAndBaby,
    routes.healthDevices,
    routes.personalCare,
    routes.offers,
  ],
};

/** The menu's tabs. Shop sits inside Services here rather than as a sixth tab. */
export const menuGroups: readonly NavigationGroup[] = [services, business, pharmacy, company, help];

/** The tab the menu opens on. Named so the menu never indexes into the array. */
export const defaultMenuGroupKey = services.key;

/**
 * The footer's Services column.
 *
 * Listed out rather than derived from the menu's Services group by filtering.
 * The two lists genuinely differ — the footer separates the shop from services
 * and names the prescription flows the menu folds together — and a derived
 * list expressed as `.slice(0, 8)` is unreadable and breaks silently when the
 * source order changes.
 */
const footerServices: NavigationGroup = {
  key: 'footer-services',
  titleKey: 'menu.services',
  contentKey: 'menu.group-services',
  links: [
    routes.consultations,
    routes.appointments,
    routes.labTests,
    routes.healthChecks,
    routes.prescriptionService,
    routes.repeatOrders,
    routes.pharmacyServices,
    routes.delivery,
    routes.collection,
    routes.careServices,
    routes.beautyWellness,
    routes.petHealth,
    routes.send,
  ],
};

/** Pharmacy OS as the footer lists it: the headline surfaces, not all twelve. */
const footerPharmacy: NavigationGroup = {
  key: 'footer-pharmacy',
  titleKey: 'menu.pharmacy',
  contentKey: 'menu.group-pharmacy',
  links: [
    routes.pharmacyOs,
    routes.osInventory,
    routes.osDispensing,
    routes.osNms,
    routes.osPatient360,
    routes.osProcurement,
    routes.penny,
    routes.apiIntegrations,
  ],
};

const footerHelp: NavigationGroup = {
  key: 'footer-help',
  titleKey: 'menu.help',
  contentKey: 'menu.group-help',
  links: [...help.links, routes.security],
};

/**
 * The footer's columns.
 *
 * Six rather than the menu's five: the footer is a site map read at leisure,
 * so the shop is worth its own column, while the tabbed menu is scanned
 * quickly and a sixth tab costs more than it gives.
 */
export const footerColumns: readonly NavigationGroup[] = [
  shop,
  footerServices,
  business,
  footerPharmacy,
  company,
  footerHelp,
];

/**
 * The menu's right-hand panel.
 *
 * The supply side of the marketplace, which is easy to lose inside a consumer
 * menu and is where the platform's revenue comes from. Kept short on purpose:
 * a panel with a dozen entries is a second menu, not a shortcut.
 */
export const menuActions: readonly Destination[] = [
  routes.listPharmacy,
  routes.joinClinician,
  routes.deliveryPartners,
  routes.suppliers,
  routes.enterprise,
];
